import { DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { EnterIcon } from 'assets/images/svg';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { peopleActions } from 'reduxStore/people/slice';
import { Avatar, Button, Col, Flex, Form, Input, List, Modal, Row, Typography } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';

const { Title, Text } = Typography;

const AddAssociatedModal = ({ open, setOpen, children, personID }) => {
	// ✅ Hooks
	const {
		token: {
			fontSizeSM,
			marginLG,
			paddingXL,
			paddingMD,
			colorTextDescription,
			paddingLG,
			paddingXS,
			paddingSM,
			colorText,
			fontWeightStrong,
			colorTextLabel
		}
	} = theme.useToken();
	const dispatch = useDispatch();

	const [form] = Form.useForm();

	// ✅ Redux state
	const { loadingAddProfile, completeAddProfile } = useSelector((state) => state.People);

	// ✅ Local state
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [internalVisible, setInternalVisible] = useState(false);

	const visible = open ?? internalVisible;
	const setVisible = setOpen ?? setInternalVisible;

	// ✅ Handle "Add Dependant" logic
	const handleAddAssociated = () => {
		dispatch(
			peopleActions.inviteAccessOnlyPeople({
				people: selectedUsers.map((user) => ({ ...user, masterUserID: personID }))
			})
		);
	};

	//✅ Function to add users by name
	const handleAddUserByName = async () => {
		try {
			const values = await form.validateFields();
			const { firstName, lastName } = values;

			// Check if user is already added
			if (!selectedUsers.some((user) => user.firstName === firstName && user.lastName === lastName)) {
				setSelectedUsers([...selectedUsers, { id: Date.now(), firstName, lastName, status: 'Pending' }]);
				form.resetFields();
			}
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	//✅ Function to clear the list
	const handleClearList = () => {
		setSelectedUsers([]);
	};

	//✅ Function to delete a specific user
	const handleDeleteUser = (id) => {
		setSelectedUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
	};

	// ✅ Close modal function (memoized)
	const handleCancel = useCallback(() => {
		setSelectedUsers([]); // Clear users when closing
		setVisible(false);
	}, [setVisible]);

	// ✅ Close modal when invite access only is successful
	useEffect(() => {
		if (completeAddProfile) handleCancel();
	}, [completeAddProfile, handleCancel]);

	return (
		<Fragment>
			<ConditionalTooltipButton
				buttonText={'Add People'}
				condition={false}
				disabledCondition={false}
				trueTitle={"Add this Person's Associates"}
				falseTitle={'Access-Only Users are not permitted to add associates.'}
				onClick={() => setVisible(true)}
			>
				{children}
			</ConditionalTooltipButton>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='flex-end'
						style={{ marginTop: marginLG }}
						gap={paddingSM}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loadingAddProfile}>
							Cancel
						</Button>
						<Button
							onClick={handleAddAssociated}
							disabled={selectedUsers.length === 0 || loadingAddProfile}
							type='primary'
							loading={loadingAddProfile}
						>
							Add People
						</Button>
					</Flex>
				]}
				width={472}
				styles={{
					content: {
						padding: paddingXL
					}
				}}
			>
				{/* ✅ Modal Content */}
				<Flex vertical gap={paddingLG}>
					<Flex vertical>
						<Title level={4} style={{ margin: 0 }}>
							Add Associated People
						</Title>
						<Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
							Adding an Associated User under a regular User 
							<br />
							automatically grants them the same access permissions <br />
							as the regular User.
						</Text>
					</Flex>

					<Flex vertical gap={24}>
						{/* Form for adding by Name */}
						<Form form={form} layout='vertical'>
							<Row gutter={[paddingXS]} align={'bottom'}>
								<Col span={11}>
									<Form.Item
										name='firstName'
										label={<Text strong>First Name</Text>}
										rules={fieldsValidator.Name}
										required={false}
									>
										<Input placeholder='Enter First Name' prefix={<UserOutlined />} />
									</Form.Item>
								</Col>
								<Col span={11}>
									<Form.Item
										name='lastName'
										label={<Text strong>Last Name</Text>}
										rules={fieldsValidator.Name}
										required={false}
									>
										<Input
											placeholder='Enter Last Name'
											prefix={<UserOutlined />}
											onPressEnter={handleAddUserByName}
										/>
									</Form.Item>
								</Col>
								<Col span={2}>
									<Form.Item label={''}>
										<Button type='primary' icon={<EnterIcon />} onClick={handleAddUserByName} />
									</Form.Item>
								</Col>

								{/* List of added users */}
								<Col span={24}>
									<Flex gap={paddingMD} vertical>
										<Flex align='center' justify='space-between'>
											<Text strong>Selected People</Text>
											<Button
												type='text'
												onClick={handleClearList}
												style={{ textDecoration: 'underline' }}
											>
												Clear List
											</Button>
										</Flex>

										<List
											dataSource={selectedUsers}
											loading={loadingAddProfile}
											style={{ minHeight: 186 }}
											renderItem={(user) => (
												<List.Item
													style={{ padding: 0, border: 0, marginBottom: paddingXS }}
													extra={
														<Button
															icon={<DeleteOutlined style={{ fontSize: 16 }} />}
															onClick={() => handleDeleteUser(user.id)}
															type='text'
															style={{ color: colorTextLabel }}
														/>
													}
												>
													<List.Item.Meta
														avatar={
															<Avatar
																size={30}
																style={{
																	fontSize: fontSizeSM,
																	color: colorText,
																	fontWeight: fontWeightStrong
																}}
															>
																{user.firstName?.charAt(0).toUpperCase()}
															</Avatar>
														}
														title={
															<Text
																style={{ fontWeight: 400 }}
															>{`${user.firstName} ${user.lastName}`}</Text>
														}
													/>
												</List.Item>
											)}
										/>
									</Flex>
								</Col>
							</Row>
						</Form>
					</Flex>
				</Flex>
			</Modal>
		</Fragment>
	);
};

export default AddAssociatedModal;
